import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonImg,
  IonLabel,
  IonPage,
  isPlatform,
} from '@ionic/react';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import {
  Redirect, useHistory, useLocation,
} from 'react-router-dom';

import AppImg from '@/assets/imgs/login-app-ads2.webp';
import LogoPepper from '@/assets/imgs/pepper-logo.svg';
import LoginScreen from '@/components/auth/LoginScreen/LoginScreen';
import NewPasswordHint from '@/components/auth/NewPasswordHint/NewPasswordHint';
import PinInputs, { PinInputsHandle } from '@/components/auth/PinInputs/PinInputs';
import { ROUTES_PATH } from '@/constants';
import useNewPassword from '@/hooks/useNewPassword';
import { ClientTaxonomyEvents } from '@/interfaces/AnalyticsInterface/AnalyticsInterface';
import { postResetPassword } from '@/redux/auth/authAPI';
import { Analytics } from '@/services/analytics';
import { clearBiometricCredentials } from '@/services/biometry';
import { storageService } from '@/services/storage';

import LoginAd from '../LoginAd/LoginAd';
import LoginError from '../LoginError/LoginError';
import styles from './ResetPassword.module.scss';

export default function ResetPassword() {
  const history = useHistory();
  const query = new URLSearchParams(useLocation().search);

  const token = query.get('Token');
  const timestamp = query.get('Timestamp');

  if (!token || !timestamp) {
    return <Redirect to={ROUTES_PATH.LOGIN} />;
  }

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [resetError, setResetError] = useState(false);

  const {
    newPassword,
    setNewPassword,
    repeatedPassword,
    setRepeatedPassword,
    isValid,
    errorMessage,
    newPasswordStatus,
    repeatedPasswordStatus,
  } = useNewPassword();
  const repeatInputRef = useRef<PinInputsHandle>(null);

  const changePasswordUser = async () => {
    // Además del botón deshabilitado: nunca se envía una clave que incumpla las reglas
    if (!isValid) return;
    try {
      await postResetPassword({ password: newPassword, authorization: token, timestamp: parseInt(timestamp) });
      Analytics.registerAnalyticsEvent({ [ClientTaxonomyEvents.PASSWORD_RESET]: { type: 'EMAIL' } });
      // La biometría guardada tiene la clave anterior: se borra y, al entrar con la
      // nueva clave, el login vuelve a ofrecer activarla
      if (storageService.getBiometrySaved() !== null) {
        await clearBiometricCredentials().catch(storageService.clearBiometrySaved);
      }
      return history.replace(ROUTES_PATH.LOGIN);
    } catch (error) {
      setResetError(true);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <IonPage className={styles.pageResetPassword}>
      {!isPlatform('android') && !isPlatform('ios') && (
        <>
          <div className={classNames(styles.LoginScreen, styles.LoginSideContent, styles.loginDesktop, styles.loginAppImg)}>
            <IonImg src={AppImg} className={styles.LoginScreenImg} />
            <LoginAd />
          </div>

          <div className={classNames(styles.LoginSideContent, styles.changePassword)}>
            <LoginScreen />
          </div>
        </>
      )}
      <div className={styles.loginInfo}>
        <div className={styles.passwords}>
          <div
            className={styles.loginMobile}
          >
            <IonImg src={LogoPepper} className={styles.LoginScreenImg} />
            <IonLabel className={styles.LoginScreenLabel}>Comercios</IonLabel>
          </div>
          <IonCard className={styles.cardPassword}>
            {resetError && <LoginError close={() => { setResetError(false); }} />}
            {!resetError && (
            <>
              <IonCardHeader className={styles.cardPasswordHeader}>
                <IonCardTitle className={styles.cardPasswordHeaderTitle}>
                  Cambiar contraseña
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent className={styles.cardPasswordContent}>
                <div>
                  <IonLabel className={styles.contentNewPassword}>
                    Introduce tu nueva clave
                  </IonLabel>
                  <PinInputs
                    id="new-password-input"
                    value={newPassword}
                    onChange={setNewPassword}
                    onComplete={() => repeatInputRef.current?.focus()}
                    status={newPasswordStatus}
                    autoFocus
                  />
                </div>
                <div>
                  <IonLabel className={styles.contentNewPassword}>
                    Repite la clave
                  </IonLabel>
                  <PinInputs
                    ref={repeatInputRef}
                    id="repeat-password-input"
                    value={repeatedPassword}
                    onChange={setRepeatedPassword}
                    status={repeatedPasswordStatus}
                  />
                </div>
                <NewPasswordHint errorMessage={errorMessage} />
                <div className={styles.buttonsResetPassword}>
                  <IonButton
                    className={styles.buttonResetPasswordSend}
                    onClick={changePasswordUser}
                    disabled={!isValid}
                  >
                    Enviar
                  </IonButton>
                </div>
              </IonCardContent>
            </>
            )}
          </IonCard>
        </div>
      </div>
    </IonPage>
  );
}
